import { FC, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiPlaceholderArea } from "@miplatform/components";
import { HTTP_STATUSES, RECORD_TYPES } from "@miplatform/constants";
import { ITransferListItem } from "@miplatform/types";
import { simpleSort } from "@miplatform/utils";
import { TransferList } from "@mibuildweb/components";
import { useIssueTemplates } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IOrderTemplateForm } from "../ConfigOrderTemplates.types";

const RECORD_TYPE_MAP = {
  recordables: RECORD_TYPES.RECORDABLE,
  warrantyItems: RECORD_TYPES.SERVICE_ITEM,
};

const LABEL_MAP = {
  recordables: "Recordables",
  warrantyItems: "Warranty Items",
};

export const OrderIssueTemplatesTabView: FC<
  OrderIssueTemplatesTabViewProps
> = ({ form, extraTabProps: { isReadOnly, view } = {} }) => {
  const { setValue, watch } = form;
  const { issueTemplates = [] } = watch();

  const recordType = RECORD_TYPE_MAP[view as keyof typeof RECORD_TYPE_MAP];
  const typeLabel = LABEL_MAP[view as keyof typeof LABEL_MAP];

  const {
    issueTemplates: allIssueTemplates,
    getIssueTemplates,
    setIssueTemplates,
  } = useIssueTemplates();
  const theme = useTheme();

  const getData = async () => {
    const response = await getIssueTemplates({
      filters: { recordType },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setIssueTemplates(response.data);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const availableRecordables: ITransferListItem[] = allIssueTemplates.map(
    (i) => ({ key: i.id, title: i.name })
  );

  const selectedRecordables: ITransferListItem[] = issueTemplates
    .filter((i) => i.recordType === recordType)
    .sort((a, b) => simpleSort(a.name, b.name))
    .map((i) => ({ key: i.id, title: i.name }));

  const handleAddItem = (key: string) => {
    const selectedRecordable = allIssueTemplates.find((d) => d.id === key);
    if (!selectedRecordable) {
      return;
    }
    const updatedRecordables = [...issueTemplates, selectedRecordable];
    setValue("issueTemplates", updatedRecordables, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveItem = (key: string) => {
    const updatedRecordables = issueTemplates.filter((d) => d.id !== key);
    setValue("issueTemplates", updatedRecordables, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <ConfigAccordionHeader title={typeLabel}>
      {allIssueTemplates.length === 0 && (
        <MiPlaceholderArea
          text={`No ${typeLabel} Created`}
          helperText={`Tip: Create your ${typeLabel} and you can see them here.`}
          showLogo
          bgColor={theme.palette.primary.light}
        />
      )}
      {allIssueTemplates.length > 0 && (
        <TransferList
          showAvailableItemsSearch
          availableItems={availableRecordables}
          selectedItems={selectedRecordables}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          availableTitle={typeLabel}
          selectedTitle={`Selected ${typeLabel}`}
          disabled={isReadOnly}
        />
      )}
    </ConfigAccordionHeader>
  );
};

interface OrderIssueTemplatesTabViewProps {
  form: UseFormReturn<IOrderTemplateForm>;
  extraTabProps?: {
    isReadOnly?: boolean;
    view?: "recordables" | "warrantyItems";
  };
}
